// Timesheet Data Types and Interfaces
// Phase 2 - Week 4: Timesheet Processing Foundation

export interface TimesheetEntry {
  id: string;
  employeeId: string;
  employeeName: string;
  contractorId?: string;
  contractorName?: string;
  
  // Date and time information
  date: Date;
  startTime: string; // HH:MM format
  endTime: string;   // HH:MM format
  breakTime?: number; // minutes
  
  // Work details
  jobSite: string;
  jobDescription: string;
  workType: 'regular' | 'overtime' | 'double_time' | 'holiday';
  
  // Rates and calculations
  hourlyRate: number;
  hoursWorked: number;
  overtimeHours?: number;
  doubleTimeHours?: number;
  totalPay: number;
  
  // Status and approval
  status: 'draft' | 'submitted' | 'approved' | 'rejected' | 'paid';
  submittedAt?: Date;
  approvedAt?: Date;
  approvedBy?: string;
  rejectionReason?: string;
  
  // Documentation
  photos?: string[]; // Firebase Storage URLs
  notes?: string;
  
  // Metadata
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
}

export interface TimesheetWeek {
  id: string;
  employeeId: string;
  employeeName: string;
  weekStarting: Date; // Monday of the week
  weekEnding: Date;   // Sunday of the week
  entries: TimesheetEntry[];
  totalHours: number;
  totalRegularHours: number;
  totalOvertimeHours: number;
  totalDoubleTimeHours: number;
  totalPay: number;
  status: 'draft' | 'submitted' | 'approved' | 'rejected' | 'paid';
  submittedAt?: Date;
  approvedAt?: Date;
  approvedBy?: string;
  rejectionReason?: string;
  
  // Metadata
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
}

export interface TimesheetFilters {
  employeeId?: string;
  employeeName?: string;
  contractorId?: string;
  contractorName?: string;
  status?: string;
  dateFrom?: Date;
  dateTo?: Date;
  jobSite?: string;
  workType?: string;
  approvedBy?: string;
}

export interface TimesheetStats {
  totalEntries: number;
  pendingApproval: number;
  approved: number;
  rejected: number;
  paid: number;
  totalHours: number;
  totalRegularHours: number;
  totalOvertimeHours: number;
  totalDoubleTimeHours: number;
  totalPay: number;
  averageHoursPerEntry: number;
  averagePayPerEntry: number;
}

export interface TimesheetFormData {
  employeeId: string;
  contractorId?: string;
  date: string; // YYYY-MM-DD format for form input
  startTime: string;
  endTime: string;
  breakTime: number;
  jobSite: string;
  jobDescription: string;
  workType: 'regular' | 'overtime' | 'double_time' | 'holiday';
  notes?: string;
  photos?: File[];
}

export interface TimesheetSortOptions {
  field: 'date' | 'employeeName' | 'jobSite' | 'hoursWorked' | 'totalPay' | 'status' | 'submittedAt' | 'approvedAt';
  direction: 'asc' | 'desc';
}

export interface TimesheetApprovalAction {
  timesheetId: string;
  action: 'approve' | 'reject';
  reason?: string; // Required for rejection
  approvedBy: string;
}

export interface TimesheetBulkAction {
  timesheetIds: string[];
  action: 'approve' | 'reject' | 'delete' | 'export';
  reason?: string; // Required for bulk rejection
  approvedBy?: string; // Required for bulk approval
}

export interface TimesheetExportOptions {
  format: 'csv' | 'excel' | 'pdf';
  dateRange: {
    start: Date;
    end: Date;
  };
  filters?: TimesheetFilters;
  includePhotos?: boolean;
  groupBy?: 'employee' | 'week' | 'contractor' | 'jobSite';
}

export interface TimesheetValidationError {
  field: string;
  message: string;
  value?: any;
}

export interface TimesheetValidationResult {
  isValid: boolean;
  errors: TimesheetValidationError[];
  warnings?: TimesheetValidationError[];
}

// Utility types for time calculations
export interface TimeCalculation {
  totalMinutes: number;
  totalHours: number;
  regularHours: number;
  overtimeHours: number;
  doubleTimeHours: number;
}

export interface PayCalculation {
  regularPay: number;
  overtimePay: number;
  doubleTimePay: number;
  totalPay: number;
  hourlyRate: number;
  overtimeRate: number; // 1.5x regular rate
  doubleTimeRate: number; // 2x regular rate
}

export interface WeekBoundaries {
  start: Date; // Monday
  end: Date;   // Sunday
  weekNumber: number;
  year: number;
}

// Constants for timesheet calculations
export const TIMESHEET_CONSTANTS = {
  REGULAR_HOURS_PER_DAY: 8,
  REGULAR_HOURS_PER_WEEK: 40,
  OVERTIME_MULTIPLIER: 1.5,
  DOUBLE_TIME_MULTIPLIER: 2.0,
  MAX_HOURS_PER_DAY: 24,
  MAX_BREAK_MINUTES: 480, // 8 hours
  MIN_BREAK_MINUTES: 0,
  WORK_TYPES: {
    REGULAR: 'regular',
    OVERTIME: 'overtime',
    DOUBLE_TIME: 'double_time',
    HOLIDAY: 'holiday'
  },
  STATUSES: {
    DRAFT: 'draft',
    SUBMITTED: 'submitted',
    APPROVED: 'approved',
    REJECTED: 'rejected',
    PAID: 'paid'
  }
} as const;

// Type guards for runtime type checking
export const isTimesheetEntry = (obj: any): obj is TimesheetEntry => {
  return obj && 
    typeof obj.id === 'string' &&
    typeof obj.employeeId === 'string' &&
    typeof obj.employeeName === 'string' &&
    obj.date instanceof Date &&
    typeof obj.startTime === 'string' &&
    typeof obj.endTime === 'string' &&
    typeof obj.jobSite === 'string' &&
    typeof obj.jobDescription === 'string' &&
    ['regular', 'overtime', 'double_time', 'holiday'].includes(obj.workType) &&
    typeof obj.hourlyRate === 'number' &&
    typeof obj.hoursWorked === 'number' &&
    typeof obj.totalPay === 'number' &&
    ['draft', 'submitted', 'approved', 'rejected', 'paid'].includes(obj.status);
};

export const isTimesheetWeek = (obj: any): obj is TimesheetWeek => {
  return obj &&
    typeof obj.id === 'string' &&
    typeof obj.employeeId === 'string' &&
    typeof obj.employeeName === 'string' &&
    obj.weekStarting instanceof Date &&
    obj.weekEnding instanceof Date &&
    Array.isArray(obj.entries) &&
    typeof obj.totalHours === 'number' &&
    typeof obj.totalPay === 'number' &&
    ['draft', 'submitted', 'approved', 'rejected', 'paid'].includes(obj.status);
};

// New types for weekly timesheet entry system

// Activity detail for each day
export interface DailyActivity {
  hours: number;
  activityCode: string;
  activityDescription: string;
}

export interface WeeklyTimesheetEntry {
  employeeId: string;
  employeeName: string;
  hours: {
    monday: number;
    tuesday: number;
    wednesday: number;
    thursday: number;
    friday: number;
    saturday: number;
    sunday: number;
  };
  // Regular/OT hour breakdown
  regularHours?: {
    monday: number;
    tuesday: number;
    wednesday: number;
    thursday: number;
    friday: number;
    saturday: number;
    sunday: number;
  };
  overtimeHours?: {
    monday: number;
    tuesday: number;
    wednesday: number;
    thursday: number;
    friday: number;
    saturday: number;
    sunday: number;
  };
  // Daily activity breakdown - each day can have multiple activities
  activities: {
    monday: DailyActivity[];
    tuesday: DailyActivity[];
    wednesday: DailyActivity[];
    thursday: DailyActivity[];
    friday: DailyActivity[];
    saturday: DailyActivity[];
    sunday: DailyActivity[];
  };
  // Legacy fields for backward compatibility
  activityCode?: string;
  activityDescription?: string;
}

export interface WeeklyTimesheet {
  id: string;
  jobName: string;
  jobNumber?: string;
  weekEnding: Date; // Sunday
  entries: WeeklyTimesheetEntry[];
  status: 'draft' | 'completed' | 'processed_for_invoice';
  
  // Contractor information
  contractorId?: string;
  contractorName?: string;
  markupPercentage?: number; // 4.00, 4.50, 25.00, etc.
  
  // Metadata
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  
  // Processing info
  processedForInvoice?: boolean;
  invoiceNumbers?: string[];
}

export interface TimesheetEntryFormData {
  jobName: string;
  jobNumber?: string;
  weekEnding: string; // YYYY-MM-DD format for form input
  entries: WeeklyTimesheetEntry[];
}

export interface EmployeeSearchResult {
  id: string;
  name: string;
  rates: {
    cec?: number;
    reliable?: number;
    btc?: number;
  };
  isActive: boolean;
}

export interface TimesheetImportData {
  jobName: string;
  jobNumber?: string;
  weekEnding: Date;
  employeeData: Array<{
    firstName: string;
    lastName: string;
    monday?: number;
    tuesday?: number;
    wednesday?: number;
    thursday?: number;
    friday?: number;
    saturday?: number;
    sunday?: number;
    // Regular/OT breakdown
    mondayRegular?: number;
    tuesdayRegular?: number;
    wednesdayRegular?: number;
    thursdayRegular?: number;
    fridayRegular?: number;
    saturdayRegular?: number;
    sundayRegular?: number;
    mondayOT?: number;
    tuesdayOT?: number;
    wednesdayOT?: number;
    thursdayOT?: number;
    fridayOT?: number;
    saturdayOT?: number;
    sundayOT?: number;
    activityCode?: string;
    activityDescription?: string;
    // CEC Rate information
    cecRate?: number; // Rate per hour from CEC timesheet
    cecAmount?: number; // Total amount for this entry (hours * rate)
    cecEarningDesc?: string; // Regular/Overtime designation from CEC
  }>;
}

export interface GustaPayrollExportData {
  employeeId: string;
  employeeName: string;
  weekEnding: Date;
  regularHours: number;
  overtimeHours: number;
  totalHours: number;
  regularPay: number;
  overtimePay: number;
  totalPay: number;
}

// Rate Comparison Interfaces
export interface EmployeeRateComparison {
  employeeId?: string;
  employeeName: string;
  firstName: string;
  lastName: string;
  
  // CEC provided rates from timesheet
  cecProvidedRate: number;
  cecEarningDesc: string; // Regular or Overtime
  cecAmount: number;
  cecHours: number;
  
  // Database rates
  databaseRegularRate?: number;
  databaseOvertimeRate?: number;
  
  // Comparison results
  rateMatch: boolean;
  rateDifference?: number; // Positive if CEC rate is higher
  rateDifferencePercentage?: number;
  
  // Recommendations
  recommendedAction: 'no_action' | 'update_employee_rate' | 'notify_cec' | 'investigate';
  actionReason: string;
}

export interface TimesheetRateAnalysis {
  timesheetId?: string;
  jobName: string;
  jobNumber?: string;
  weekEnding: Date;
  contractorName: string;
  
  // Overall analysis
  totalEmployees: number;
  employeesWithRateDiscrepancies: number;
  totalRateDiscrepancyAmount: number;
  
  // Individual employee comparisons
  employeeComparisons: EmployeeRateComparison[];
  
  // Summary statistics
  averageRateDifference: number;
  maxRateDifference: number;
  minRateDifference: number;
  
  // Analysis metadata
  analyzedAt: Date;
  analyzedBy: string;
}
